import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export type HoverRange = { start: number; end: number };

/**
 * Which grid index the pointer is over, and whether Shift was held on the event
 * that reported it.
 *
 * `shiftKey` is carried alongside the index rather than tracked separately so the
 * range preview reads the same modifier state the click handler does (TA-10163).
 */
export type HoverState = { index: number; shiftKey: boolean };

export const NO_HOVER: HoverState = { index: -1, shiftKey: false };

/**
 * Range of indices to preview-highlight while Shift is held.
 * Returns null when there is nothing to preview.
 */
export const computeHoverRange = (
  shiftKey: boolean,
  anchorIndex: number | null,
  hoveredIndex: number
): HoverRange | null => {
  if (!shiftKey || anchorIndex === null || hoveredIndex < 0) return null;

  return {
    start: Math.min(anchorIndex, hoveredIndex),
    end: Math.max(anchorIndex, hoveredIndex),
  };
};

export const isIndexInRange = (index: number, range: HoverRange | null) =>
  !!range && index >= range.start && index <= range.end;

/**
 * Reads Shift off either event shape this hook can be fed.
 *
 * React DOM mouse events (what react-native-web forwards `onMouseEnter`/
 * `onMouseMove` to) expose `shiftKey` at the top level. React Native press and
 * pointer events only carry it on `nativeEvent`. Normalising here keeps the hook
 * usable from both without every call site having to know the difference.
 *
 * `??` rather than `||` so a genuine top-level `false` is respected instead of
 * falling through to `nativeEvent`.
 */
export const readShiftKey = (event: any): boolean =>
  !!(event?.shiftKey ?? event?.nativeEvent?.shiftKey);

/**
 * Tracks the hovered index and Shift state behind a Shift-range preview.
 *
 * Shift is read from the pointer event passed to `onItemHover` as well as from
 * window keydown/keyup, because keyboard state alone is not reliable: the page
 * misses the keydown entirely when focus sits outside the document, and a window
 * `blur` clears it while Shift is still physically held. Either left the preview
 * stuck off while Shift-*click* kept working, since the click reads `shiftKey`
 * straight off its own event (TA-10163). Every pointer event now re-asserts the
 * truth, so the preview recovers on the next mouse move.
 *
 * Keyboard listeners are retained so pressing Shift without moving the mouse
 * still reveals the preview.
 */
export const useRangeHoverState = (anchorIndex: number | null) => {
  const [hoverState, setHoverState] = useState<HoverState>(NO_HOVER);
  // Mirrors hoverState so the high-frequency mouse-move path can skip setState
  // outright when nothing changed, rather than leaning on React's bail-out
  // (which can still re-render the consumer once per event).
  const hoverStateRef = useRef<HoverState>(NO_HOVER);

  const applyHoverState = useCallback((next: HoverState) => {
    const prev = hoverStateRef.current;
    if (prev.index === next.index && prev.shiftKey === next.shiftKey) return;
    hoverStateRef.current = next;
    setHoverState(next);
  }, []);

  /** Call from both onMouseEnter and onMouseMove, passing `event.shiftKey`. */
  const onItemHover = useCallback(
    (index: number, shiftKey: boolean) => applyHoverState({ index, shiftKey }),
    [applyHoverState]
  );

  /**
   * Call from onMouseLeave. Guarded by index so a leave arriving after the next
   * item's enter cannot wipe the incoming hover.
   */
  const onItemHoverOut = useCallback(
    (index: number) => {
      if (hoverStateRef.current.index === index) applyHoverState(NO_HOVER);
    },
    [applyHoverState]
  );

  useEffect(() => {
    // Capability check rather than a Platform test: this hook lives in the shared
    // hooks directory, and `globalThis.addEventListener` is undefined on native.
    // Pointer hover has no meaning there anyway, so a no-op is correct.
    if (typeof globalThis.addEventListener !== 'function') return;

    const setShiftKey = (shiftKey: boolean) =>
      applyHoverState({ ...hoverStateRef.current, shiftKey });
    const onKeyDown = (ev: KeyboardEvent) => {
      if (ev.key === 'Shift') setShiftKey(true);
    };
    const onKeyUp = (ev: KeyboardEvent) => {
      if (ev.key === 'Shift') setShiftKey(false);
    };
    const onBlur = () => setShiftKey(false);

    globalThis.addEventListener('keydown', onKeyDown);
    globalThis.addEventListener('keyup', onKeyUp);
    globalThis.addEventListener('blur', onBlur);
    return () => {
      globalThis.removeEventListener('keydown', onKeyDown);
      globalThis.removeEventListener('keyup', onKeyUp);
      globalThis.removeEventListener('blur', onBlur);
    };
  }, [applyHoverState]);

  const hoverRange = useMemo(
    () => computeHoverRange(hoverState.shiftKey, anchorIndex, hoverState.index),
    [hoverState.shiftKey, hoverState.index, anchorIndex]
  );

  const isInHoverRange = useCallback(
    (index: number) => isIndexInRange(index, hoverRange),
    [hoverRange]
  );

  return { hoverRange, isInHoverRange, onItemHover, onItemHoverOut };
};
